/**
 * Auth clinic-administration surfaces (verified against Auth source
 * @ efa2303): members, employee onboarding (temp password returned exactly
 * once), suspend/resume, roles + grants, and the permission catalogue.
 * These run against the Auth origin with the signed-in admin's Bearer.
 */

import { apiRequest } from '../api/http';

const enc = encodeURIComponent;

/* -------------------------------- members ---------------------------------- */

export interface MemberRow {
  uid: string;
  status: 'active' | 'suspended' | (string & {});
  startedAt: string;
  suspendedAt?: string;
  suspendedReason?: string | null;
  user: {
    username: string | null;
    displayName: string | null;
    phone: string | null;
    email: string | null;
  };
  roles: { id: string; name: string }[];
  directPermissions: string[];
  /** FULL Clinic Admin (platform-governed direct grant). */
  admin: boolean;
}

export const listMembers = (clinicId: string) =>
  apiRequest<MemberRow[]>({ base: 'auth', path: `/clinics/${enc(clinicId)}/members` });

export interface AddEmployeeResult {
  uid: string;
  clinicId: string;
  created: boolean;
  username?: string;
  roles: string[];
  directPermissions: string[];
  /** Present ONLY for a newly created account, exactly once. */
  tempPassword?: string;
}

export const addEmployee = (
  clinicId: string,
  body: {
    mobile: string;
    firstName: string;
    lastName: string;
    roleIds: string[];
    email?: string;
    nationalId?: string;
    gender?: string;
    /** From the EXISTING_ACCOUNT_CONFIRMATION_REQUIRED answer, once the admin confirmed the person (tasks#142). */
    confirmExistingToken?: string;
  },
) =>
  apiRequest<AddEmployeeResult>({
    base: 'auth',
    method: 'POST',
    path: `/clinics/${enc(clinicId)}/employees`,
    body,
  });

export const suspendMember = (clinicId: string, uid: string, reason?: string) =>
  apiRequest<unknown>({
    base: 'auth',
    method: 'POST',
    path: `/clinics/${enc(clinicId)}/members/${enc(uid)}/suspend`,
    body: reason ? { reason } : {},
  });

export const resumeMember = (clinicId: string, uid: string) =>
  apiRequest<unknown>({
    base: 'auth',
    method: 'POST',
    path: `/clinics/${enc(clinicId)}/members/${enc(uid)}/resume`,
    body: {},
  });

/* --------------------------------- roles ----------------------------------- */

export interface RoleRow {
  id: string;
  name: string;
  description?: string | null;
  active?: boolean;
  permissions?: string[];
  members?: number;
  [k: string]: unknown;
}

export const listRoles = (clinicId: string) =>
  apiRequest<RoleRow[]>({ base: 'auth', path: `/clinics/${enc(clinicId)}/roles` });

export const createRole = (
  clinicId: string,
  body: { name: string; description?: string; permissions?: string[] },
) =>
  apiRequest<RoleRow>({
    base: 'auth',
    method: 'POST',
    path: `/clinics/${enc(clinicId)}/roles`,
    body,
  });

export const updateRole = (
  clinicId: string,
  roleId: string,
  body: { name?: string; description?: string; active?: boolean; permissions?: string[] },
) =>
  apiRequest<RoleRow>({
    base: 'auth',
    method: 'PATCH',
    path: `/clinics/${enc(clinicId)}/roles/${enc(roleId)}`,
    body,
  });

export const assignRole = (clinicId: string, uid: string, roleId: string) =>
  apiRequest<unknown>({
    base: 'auth',
    method: 'POST',
    path: `/clinics/${enc(clinicId)}/members/${enc(uid)}/roles`,
    body: { roleId },
  });

export const removeRole = (clinicId: string, uid: string, roleId: string) =>
  apiRequest<unknown>({
    base: 'auth',
    method: 'DELETE',
    path: `/clinics/${enc(clinicId)}/members/${enc(uid)}/roles/${enc(roleId)}`,
  });

export const addDirectPermission = (clinicId: string, uid: string, permission: string) =>
  apiRequest<unknown>({
    base: 'auth',
    method: 'POST',
    path: `/clinics/${enc(clinicId)}/members/${enc(uid)}/permissions`,
    body: { permission },
  });

export const removeDirectPermission = (clinicId: string, uid: string, permission: string) =>
  apiRequest<unknown>({
    base: 'auth',
    method: 'DELETE',
    path: `/clinics/${enc(clinicId)}/members/${enc(uid)}/permissions/${enc(permission)}`,
  });

/* ---------------------------- permission catalog ---------------------------- */

export interface PermissionRow {
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  active: boolean;
  system: boolean;
  grantPolicy: 'platform' | 'clinic_admin' | 'clinic';
}

export const permissionCatalog = () =>
  apiRequest<PermissionRow[]>({ base: 'auth', path: '/clinics/permissions' });
